import { useTranslation } from 'react-i18next';
import { ExternalLink, Headphones, PlayCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const SPOTIFY_SHOW_URL = 'https://open.spotify.com/show/4jY5GZY8pexXwjJ5J5KPlt?si=hsrmhXtyRQao593yfM9fCg';
const SPOTIFY_EMBED_URL = 'https://open.spotify.com/embed/show/4jY5GZY8pexXwjJ5J5KPlt?utm_source=generator';
const YOUTUBE_CHANNEL_URL = 'https://youtube.com/professionalchameleon';
const YOUTUBE_EMBED_URL = 'https://www.youtube-nocookie.com/embed?listType=user_uploads&list=professionalchameleon';

const PLATFORM_LINKS = [
  { label: 'Spotify', href: SPOTIFY_SHOW_URL },
  { label: 'YouTube', href: YOUTUBE_CHANNEL_URL },
  { label: 'Apple Podcasts', href: 'https://podcasts.apple.com/us/podcast/professional-chameleon/id1895020478' },
  { label: 'iHeart', href: 'https://iheart.com/podcast/331039271/' },
  { label: 'Amazon Music', href: 'https://music.amazon.com/podcasts/bd906130-be59-45cf-aa20-60cbd33461ee/professional-chameleon' },
];

const PodcastPage = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith('en');

  const copy = isEnglish
    ? {
        eyebrow: 'Podcast',
        title: 'Professional Chameleon',
        intro:
          'Watch the show on YouTube, explore the full episode feed on Spotify, and jump to your favorite podcast platform from one place.',
        youtubeTitle: 'Latest videos on YouTube',
        youtubeBody:
          'This player is set up to show the channel uploads so visitors can watch directly here or open the full channel.',
        youtubeCta: 'Open YouTube channel',
        spotifyTitle: 'Listen to every episode on Spotify',
        spotifyBody:
          'The Spotify embed lets visitors browse the show and play episodes without leaving the page, while the button below opens the full show in Spotify.',
        spotifyCta: 'Open in Spotify',
        platformsTitle: 'Listen everywhere',
        platformsBody: 'Prefer another app? Use any of the podcast links below.',
      }
    : {
        eyebrow: 'Podcast',
        title: 'Professional Chameleon',
        intro:
          'Mira el show en YouTube, explora todos los episodios en Spotify y entra a tu plataforma favorita desde un solo lugar.',
        youtubeTitle: 'Videos recientes en YouTube',
        youtubeBody:
          'Este reproductor está configurado para mostrar las subidas del canal, para que puedan ver el contenido aquí o abrir el canal completo.',
        youtubeCta: 'Abrir canal de YouTube',
        spotifyTitle: 'Escucha todos los episodios en Spotify',
        spotifyBody:
          'El embed de Spotify permite explorar el podcast y reproducir episodios desde la página, y el botón de abajo abre el show completo en Spotify.',
        spotifyCta: 'Abrir en Spotify',
        platformsTitle: 'Escúchalo donde prefieras',
        platformsBody: 'Si prefieren otra app, aquí están todos los enlaces del podcast.',
      };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0F1C2E]">
      <Navbar />

      <main className="pt-20">
        <section className="relative overflow-hidden bg-[#0F1C2E] text-white">
          <div
            className="absolute -top-24 right-0 h-80 w-80 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(125,70,140,0.35) 0%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-0 left-0 h-72 w-72 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(78,191,191,0.25) 0%, transparent 70%)' }}
          />

          <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="max-w-3xl">
              <div className="mb-5 flex items-center gap-3">
                <div className="h-2.5 w-2.5 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
                <span className="font-poppins text-xs font-semibold uppercase tracking-[0.35em] text-[#4EBFBF]">
                  {copy.eyebrow}
                </span>
              </div>

              <h1 className="font-poppins text-5xl font-black leading-tight text-white sm:text-6xl">
                {copy.title}
              </h1>

              <p className="mt-6 max-w-2xl font-source-sans text-lg leading-8 text-white/72">
                {copy.intro}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                {PLATFORM_LINKS.map((platform) => (
                  <a
                    key={platform.label}
                    href={platform.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-poppins font-medium text-white/90 transition-colors hover:border-[#4EBFBF]/50 hover:text-white"
                  >
                    {platform.label}
                    <ExternalLink size={14} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#F7F8FB] px-4 py-16 dark:bg-[#101B2B] sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-[32px] bg-white p-8 shadow-[0_20px_60px_rgba(15,28,46,0.08)] dark:bg-[#162338]">
              <div className="mb-5 flex items-center gap-3">
                <PlayCircle className="text-[#F2785C]" size={20} />
                <h2 className="font-poppins text-2xl font-bold text-[#233559] dark:text-white">
                  {copy.youtubeTitle}
                </h2>
              </div>
              <p className="font-source-sans text-base leading-7 text-[#233559]/70 dark:text-white/65">
                {copy.youtubeBody}
              </p>

              <a
                href={YOUTUBE_CHANNEL_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F2785C] px-5 py-3 font-poppins text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                {copy.youtubeCta}
                <ExternalLink size={15} />
              </a>
            </div>

            <div className="overflow-hidden rounded-[32px] border border-black/5 bg-black shadow-[0_20px_60px_rgba(15,28,46,0.14)] dark:border-white/10">
              <div className="aspect-video">
                <iframe
                  className="h-full w-full"
                  src={YOUTUBE_EMBED_URL}
                  title="Professional Chameleon YouTube"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white px-4 py-16 dark:bg-[#0F1C2E] sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="overflow-hidden rounded-[32px] border border-black/6 bg-[#1DB954] p-[1px] shadow-[0_20px_60px_rgba(29,185,84,0.16)] dark:border-white/8">
              <div className="rounded-[31px] bg-white dark:bg-[#121C2C]">
                <iframe
                  style={{ borderRadius: '31px' }}
                  src={SPOTIFY_EMBED_URL}
                  width="100%"
                  height="720"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  title="Professional Chameleon on Spotify"
                />
              </div>
            </div>

            <div className="flex flex-col justify-center rounded-[32px] bg-[#F7F8FB] p-8 dark:bg-[#162338]">
              <div className="mb-5 flex items-center gap-3">
                <Headphones className="text-[#1DB954]" size={20} />
                <h2 className="font-poppins text-2xl font-bold text-[#233559] dark:text-white">
                  {copy.spotifyTitle}
                </h2>
              </div>

              <p className="font-source-sans text-base leading-7 text-[#233559]/70 dark:text-white/65">
                {copy.spotifyBody}
              </p>

              <a
                href={SPOTIFY_SHOW_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[#1DB954] px-5 py-3 font-poppins text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                {copy.spotifyCta}
                <ExternalLink size={15} />
              </a>

              <div className="mt-10 border-t border-black/8 pt-8 dark:border-white/10">
                <h3 className="font-poppins text-lg font-bold text-[#233559] dark:text-white">
                  {copy.platformsTitle}
                </h3>
                <p className="mt-2 font-source-sans text-sm leading-6 text-[#233559]/65 dark:text-white/60">
                  {copy.platformsBody}
                </p>

                <div className="mt-5 flex flex-wrap gap-3">
                  {PLATFORM_LINKS.map((platform) => (
                    <a
                      key={`secondary-${platform.label}`}
                      href={platform.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-[#233559]/10 bg-white px-4 py-2 text-sm font-poppins font-medium text-[#233559] transition-colors hover:border-[#7D468C]/35 hover:text-[#7D468C] dark:border-white/12 dark:bg-white/5 dark:text-white/85"
                    >
                      {platform.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PodcastPage;
